import React, { useState } from 'react';
import { GameButton } from '@/components/ui';
import { ObstacleTile } from '@/components/game';
import '@/scss/test-screen.scss';

interface TestScreenProps {
  onGoHome: () => void;
}

// Define visual styles
const visualStyles = [
  {
    id: 'pixel-retro',
    name: 'Pixel Retro',
    description: '8-bit style with sharp pixels and limited color palette',
    className: 'style-pixel-retro'
  },
  {
    id: 'modern-ui',
    name: 'Modern UI',
    description: 'Vibrant gradients and floating animations like our homescreen',
    className: 'style-modern-ui'
  },
  {
    id: 'hand-drawn',
    name: 'Hand Drawn',
    description: 'Sketchy pencil art with wobbly lines and crosshatching',
    className: 'style-hand-drawn'
  },
  {
    id: 'clay-world',
    name: 'Clay World',
    description: 'Chunky plasticine figures with fingerprint textures',
    className: 'style-clay-world'
  },
  {
    id: 'neon-noir',
    name: 'Neon Noir',
    description: 'Dark cyberpunk streets with glowing neon signs',
    className: 'style-neon-noir'
  }
];

// Define enemy types
const enemyTypes = [
  { id: 'slime', name: 'Slime', behavior: 'Bounces randomly', damage: 1 },
  { id: 'spike', name: 'Spike Trap', behavior: 'Static hazard', damage: 2 },
  { id: 'turret', name: 'Turret', behavior: 'Shoots projectiles', damage: 1 },
  { id: 'ghost', name: 'Ghost', behavior: 'Phases through walls', damage: 1 },
  { id: 'spider', name: 'Spider', behavior: 'Follows player paths', damage: 1 },
  { id: 'bat', name: 'Bat', behavior: 'Flies in patterns', damage: 1 },
  { id: 'snake', name: 'Snake', behavior: 'Patrols areas', damage: 2 },
  { id: 'golem', name: 'Golem', behavior: 'Slow but strong', damage: 3 },
  { id: 'wizard', name: 'Evil Wizard', behavior: 'Teleports and casts', damage: 2 },
  { id: 'mimic', name: 'Mimic', behavior: 'Disguised as treasure', damage: 2 },
  { id: 'bomber', name: 'Bomber', behavior: 'Explodes on contact', damage: 3 },
  { id: 'iceElemental', name: 'Ice Elemental', behavior: 'Freezes tiles', damage: 1 }
];

const TestScreen: React.FC<TestScreenProps> = ({ onGoHome }) => {
  const [currentStyle, setCurrentStyle] = useState(0);
  const style = visualStyles[currentStyle];

  const nextStyle = () => {
    setCurrentStyle((prev) => (prev + 1) % visualStyles.length);
  };

  const prevStyle = () => {
    setCurrentStyle((prev) => (prev - 1 + visualStyles.length) % visualStyles.length);
  };

  return (
    <div className={`test-screen ${style.className}`}>
      <div className="test-header">
        <GameButton
          onClick={onGoHome}
          variant="secondary"
          icon="home"
          text="Back Home"
          className="back-btn"
        />
        <h1>Visual Style Test</h1>
        <div className="style-info">
          <h2>{style.name}</h2>
          <p>{style.description}</p>
        </div>
      </div>

      <div className="style-controls">
        <GameButton
          onClick={prevStyle}
          variant="info"
          icon="arrowLeft"
          text="Previous Style"
        />
        <span className="style-indicator">
          Style {currentStyle + 1} of {visualStyles.length}
        </span>
        <GameButton
          onClick={nextStyle}
          variant="info"
          icon="arrowRight"
          text="Next Style"
        />
      </div>

      <div className="test-content">
        {/* Player Character Section */}
        <section className="test-section">
          <h3>Player Character</h3>
          <div className="character-showcase">
            <div className="character-item">
              <div className="game__character"></div>
              <span>Default</span>
            </div>
            <div className="character-item">
              <div className="game__character moving-right"></div>
              <span>Moving</span>
            </div>
            <div className="character-item">
              <div className="game__character idle"></div>
              <span>Idle</span>
            </div>
            <div className="character-item">
              <div className="game__character damaged"></div>
              <span>Damaged</span>
            </div>
          </div>
        </section>

        {/* Obstacles Section */}
        <section className="test-section">
          <h3>Obstacles</h3>
          <div className="obstacles-grid">
            <div className="obstacle-item">
              <ObstacleTile type="rock" size={80} />
              <span>Rock</span>
            </div>
            <div className="obstacle-item">
              <ObstacleTile type="lava" size={80} />
              <span>Lava</span>
            </div>
            <div className="obstacle-item">
              <ObstacleTile type="stop" size={80} />
              <span>Ice/Stop</span>
            </div>
            <div className="obstacle-item">
              <ObstacleTile type="start" size={80} />
              <span>Start</span>
            </div>
            <div className="obstacle-item">
              <ObstacleTile type="end" size={80} />
              <span>Goal</span>
            </div>
            <div className="obstacle-item">
              <ObstacleTile type="teleporter" size={80} />
              <span>Teleporter</span>
            </div>
          </div>
        </section>

        {/* Enemies Section */}
        <section className="test-section">
          <h3>Enemies</h3>
          <div className="enemies-grid">
            {enemyTypes.map(enemy => (
              <div key={enemy.id} className="enemy-item">
                <div className={`enemy enemy-${enemy.id}`}>
                  <div className="enemy-sprite"></div>
                </div>
                <span className="enemy-name">{enemy.name}</span>
                <span className="enemy-behavior">{enemy.behavior}</span>
                <span className="enemy-damage">DMG: {enemy.damage}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Walls/Tiles Section */}
        <section className="test-section">
          <h3>Tiles & Walls</h3>
          <div className="tiles-grid">
            <div className="tile-item">
              <div className="game__cell"></div>
              <span>Floor</span>
            </div>
            <div className="tile-item">
              <div className="game__cell game__cell--wall"></div>
              <span>Wall</span>
            </div>
            <div className="tile-item">
              <div className="game__cell game__cell--cracked"></div>
              <span>Cracked</span>
            </div>
            <div className="tile-item">
              <div className="game__cell game__cell--grass"></div>
              <span>Grass</span>
            </div>
            <div className="tile-item">
              <div className="game__cell game__cell--water"></div>
              <span>Water</span>
            </div>
            <div className="tile-item">
              <div className="game__cell game__cell--bridge"></div>
              <span>Bridge</span>
            </div>
          </div>
        </section>

        {/* Collectibles Section */}
        <section className="test-section">
          <h3>Collectibles</h3>
          <div className="collectibles-grid">
            <div className="collectible-item">
              <div className="collectible collectible-coin"></div>
              <span>Coin</span>
            </div>
            <div className="collectible-item">
              <div className="collectible collectible-gem"></div>
              <span>Gem</span>
            </div>
            <div className="collectible-item">
              <div className="collectible collectible-key"></div>
              <span>Key</span>
            </div>
            <div className="collectible-item">
              <div className="collectible collectible-heart"></div>
              <span>Heart</span>
            </div>
            <div className="collectible-item">
              <div className="collectible collectible-star"></div>
              <span>Star</span>
            </div>
            <div className="collectible-item">
              <div className="collectible collectible-powerup"></div>
              <span>Power-up</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TestScreen;